import { ReactNode, useState } from "react";
import {
  Dialog,
  DialogNavigation,
  DialogContent,
} from "../../../../components/modal/Dialog";
import { Button, Icon } from "@sellar-io/components";
import { useCreditProcessorInitiatePayment } from "../../../../hooks/api/v2/creditProcessor/useCreditProcessorInitiatePayment";
import { apiV2AssertError } from "../../../../hooks/api/v2/apiV2Request";
import { useToast } from "../../../../components/toast/useToast.shared";
import { Toast } from "../../../../components/toast/Toast";
import { Alert } from "../../../../components/alert/Alert";
import { SpinnerLegacy } from "../../../../components";

export type PayWithSellarCreditButtonProps = {
  orderId: number;
  cancelOrderButtonProps: {
    loading: boolean;
    disabled: boolean;
    onClick: (orderId: number) => void;
  };
  renderTrigger: (props: {
    loading: boolean;
    disabled: boolean;
    onClick: () => void;
  }) => ReactNode;
  onSuccess: (params: { orderId: number; paymentUrl?: string }) => void;
  onError: (errorMessage: string) => void;
  onLoading?: (loading: boolean) => void;
};

export const PayWithSellarCreditButton = ({
  orderId,
  cancelOrderButtonProps,
  renderTrigger,
  onLoading,
  onSuccess,
  onError,
}: PayWithSellarCreditButtonProps) => {
  const { toast } = useToast();
  const creditProcessorInitiatePaymentMutation =
    useCreditProcessorInitiatePayment();
  const [openDialog, setOpenDialog] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const handleDismiss = () => {
    if (isLoading) {
      return;
    }
    setOpenDialog(false);
  };

  const handleSetLoading = (loading: boolean) => {
    setIsLoading(loading);
    onLoading?.(loading);
  };

  const handleInitiatePayment = () => {
    handleSetLoading(true);
    setOpenDialog(true);
    setErrorMessage("");

    if (!orderId) {
      setErrorMessage("Missing order id");
      handleSetLoading(false);
      return;
    }

    creditProcessorInitiatePaymentMutation.mutate(
      {
        orderId,
      },
      {
        onSuccess: (response) => {
          handleDismiss();
          toast({
            placement: "bottom",
            render: () => (
              <Toast icon="CHECK_CIRCLE">Sellar Credit payment completed</Toast>
            ),
          });
          onSuccess(response);
        },
        onError: (error) => {
          const message =
            apiV2AssertError(error)?.response?.data.message ??
            "Unable to complete this Sellar Credit payment";
          setErrorMessage(message);
          onError(message);
        },
        onSettled: () => {
          handleSetLoading(false);
        },
      },
    );
  };

  const renderContent = () => {
    if (errorMessage) {
      return (
        <div className="flex flex-col gap-4">
          <span>
            Your payment with Sellar Credit could not be processed at this time.
            Try again later or cancel the order.
          </span>
          <div className="flex justify-end gap-2">
            <Button
              disabled={cancelOrderButtonProps.disabled}
              loading={cancelOrderButtonProps.loading}
              onClick={() => {
                cancelOrderButtonProps.onClick(orderId);
              }}
              color="transparent"
            >
              Cancel Order
            </Button>
            <Button onClick={handleDismiss} color="primary">
              Go Back
            </Button>
          </div>
        </div>
      );
    }

    return (
      <div className="flex min-h-8 items-center justify-center">
        <SpinnerLegacy />
      </div>
    );
  };

  return (
    <>
      {renderTrigger({
        onClick: handleInitiatePayment,
        loading: isLoading,
        disabled: isLoading,
      })}
      <Dialog
        active={openDialog}
        onDismiss={handleDismiss}
        className="m-4 sm:h-fit sm:max-h-4/5"
      >
        <DialogNavigation
          leftIcon="CHEVRON_DOWN"
          onClickLeftIcon={handleDismiss}
          title="Pay with Sellar Credit"
          description=""
        />
        <DialogContent>
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-4">
              {isLoading && <PaymentInProgressBanner />}
              {Boolean(errorMessage) && (
                <Alert type="error" message={errorMessage} />
              )}
            </div>
            {renderContent()}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

const PaymentInProgressBanner = () => {
  return (
    <div className="flex items-center gap-2 rounded-lg border border-zinc-300 bg-zinc-100 p-4">
      <Icon size={0.7} icon="TIMER_SAND_EMPTY" />
      <p className="text-sm font-semibold text-zinc-800">
        Payment in progress. Please do not close this tab
      </p>
    </div>
  );
};
